import { useFindFirst } from "@gadgetinc/react";
import { useLocation } from "react-router-dom";
import { createContext, useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from "react";
import { api } from "../api";
import { trialCalculations } from "../utilities";
/**
 * Direct imports, not barrels.
 *
 * BillingSkeleton is the Suspense fallback for the paywall's <BillingPage/> below. It used to
 * be RouteLoader, which contradicted routes/billing.jsx:450-464 — that file renders
 * BillingSkeleton at its OWN gate and states "the two gates being identical is the entire
 * point". A merchant hitting the paywall therefore saw a centred spinner hand off to a
 * skeleton of a different shape. Splitting billing.jsx's boot read off its WebSocket query
 * made its own gate resolve much faster, which only made the mismatched frame a larger share
 * of what that merchant actually sees.
 *
 * Costs no extra bytes: components/App.jsx already imports BillingSkeleton eagerly as the
 * /billing route's fallback, so it is in the entry graph either way.
 */
import BillingSkeleton from "../components/BillingSkeleton";
// Primed inside the BillingPage lazy() factory below — see the note there.
import { primeAppHandle } from "../utils/appHandle";
/**
 * The boot shell — the same picture index.html painted statically inside #root, and the
 * same one the router and auth gates in components/App.jsx render. This gate is the LAST
 * of the three, so swapping its spinner for the shell is what makes the whole boot a
 * single unchanging frame.
 */
import BootShell, { bootShellVariant } from "../components/BootShell";
// Same reason — direct path, not a barrel. This costs no extra bytes: App.jsx
// already imports CampaignEditorSkeleton eagerly (it is the Suspense fallback
// for the /popup route), so the chunk is in the entry graph either way.
import CampaignEditorSkeleton from "../components/CampaignEditor/CampaignEditorSkeleton";
import { markBoot } from "../utils/bootTiming";

/**
 * Lazy load BillingPage to avoid circular dependency with providers/index.js.
 *
 * ⚠️ THIS IS A SECOND, SEPARATE lazy() FROM components/App.jsx:340-343, and the difference
 * used to be invisible. App.jsx's factory calls `primeAppHandle()` so the hosted-pricing URL
 * is requested in parallel with the chunk download; this one did not, so on the PAYWALL path
 * the handle was only requested from billing.jsx's mount effect — strictly after the chunk
 * had downloaded, parsed and committed.
 *
 * That was hidden while billing.jsx gated paint on a WebSocket query. Now that its boot read
 * is plain HTTP (see the split at the top of routes/billing.jsx), the page paints fast and
 * the missing prime becomes the visible bottleneck: a merchant who has just been paywalled
 * sees a fully rendered plan page with "Change your plan" stuck in its loading state.
 *
 * Priming here costs nothing — `primeAppHandle` is module-cached and never rejects — and it
 * must NOT be awaited, for the same reason App.jsx documents: awaiting would gate the route's
 * mount on it, which is the opposite of the point.
 */
const BillingPage = lazy(() => {
  primeAppHandle();
  return import("../routes/billing");
});

export const ShopContext = createContext({});

/**
 * Paths that must render even when the paywall would otherwise fire.
 *
 * /welcome is the Shopify App Pricing return landing. It runs
 * syncShopSubscription, which is what WRITES the subscription state this gate
 * READS — so gating it deadlocks the merchant who just paid. See the comment at
 * the gate itself.
 */
const BILLING_EXEMPT_PATHS = new Set(["/welcome"]);

/**
 * Paths allowed to MOUNT while the shop query is still in flight.
 *
 * ⚠️ THIS IS NOT A BILLING EXEMPTION. Nothing here weakens the paywall: the
 * moment `shop` lands, the gate below evaluates exactly as it always has, on
 * the same data, for the same route. The ONLY thing this set changes is WHEN
 * the subtree is allowed to mount — early, behind an opaque full-viewport
 * cover — so that its own queries (routes/popup.jsx's campaign fetch) can fly
 * in parallel with the shop query instead of strictly after it.
 *
 * /popup is the App Bridge modal realm. It boots in its own iframe with its own
 * JS realm, so its editor chunks and its campaign query are both serialised
 * behind auth → shop today. It is also the only route where the wait is a
 * full-screen blank: the merchant has already clicked "edit" and is staring at
 * an empty modal.
 *
 * "/" is here for exactly the same reason, and it is the route where it costs
 * the most: the dashboard's campaigns query used to be the THIRD serial hop of
 * the boot (auth → shop → campaigns), and a no-op Gadget query measures 320 ms
 * p50 / 2.1 s p90 in production. routes/index.jsx now derives a shop id from
 * the session read when `shop` has not landed yet, so mounting early is what
 * lets that query fly ALONGSIDE this one instead of strictly after it.
 *
 * Three hard requirements come with membership, all enforced below:
 *   1. The paywall condition must survive `shop === undefined` (see the
 *      leading `shop &&` at the gate) — otherwise an early mount renders
 *      BillingPage instead of the route.
 *   2. The cover must be a SIBLING of {children}, never a wrapper.
 *   3. The route must not COMMIT real content against a missing `shop` — every
 *      read has to be optional-chained, and a first render built from the
 *      resulting blanks (empty domain, null trial days, an upgrade banner shown
 *      to a payer) is wasted work at best. routes/index.jsx therefore holds its
 *      dashboard skeleton on `!shop` even once its own query has landed.
 *
 * ⚠️ Membership does NOT weaken the paywall. Production has installed shops held
 * in-app solely by `subscriptionStatus === 'active'`, so a stale read would
 * paywall a paying merchant. Nothing here is VISIBLE until `shop` lands — that is
 * what the cover is for — and the gate below evaluates on exactly the same data
 * as before.
 *
 * The boot read is a PLAIN HTTP query as of 2026-08-17; a separate `live: true`
 * query refreshes the billing fields without gating paint. Both are at the top of
 * the component, and the reasoning for the split — including why the live one
 * cannot simply be deleted — is documented there.
 */
/*
 * "/integrations" and "/data-privacy" joined on 2026-08-18, for the same reason as "/" and
 * for a cost that is easy to under-read: the gate below returns <BootShell/> INSTEAD OF
 * `children`, and `children` is where <Outlet/> lives — so a non-member route's lazy()
 * factory never runs. Its chunk is not merely late, it is NOT REQUESTED, and none of its
 * queries can start, until the shop query settles (320 ms p50 / 2.1 s p90).
 *
 * /integrations is the clearest case in the app: a 3.6 kB chunk and one unfiltered query,
 * with essentially all of its latency coming from that wait.
 *
 * ⚠️ "/analytics" was EVALUATED AND DELIBERATELY EXCLUDED. Mounting it early fires ~13
 * metered PostHog/HogQL queries (9 from /shop-analytics + 4 from /campaign-insights) from
 * mount effects, against a documented 1,200/hour ceiling. Because the paywall below is
 * `shop && …`, a paywalled merchant would fire all 13 and THEN be swapped to <BillingPage/>;
 * worse, they also fire on the shop-query FAILURE path, where today they are impossible.
 * Its chunk win is bought instead by the per-pathname warm-up in components/App.jsx.
 *
 * ⚠️ Requirement 3 below ("must not COMMIT real content against a missing shop") is not
 * free for members. routes/integrations.jsx derives `isFreePlan` from `shop?.currentPlan`,
 * which is TRUE when shop is undefined — and its own query settles faster than the shop
 * query, so without a `!shop` hold it commits an upgrade wall to a paying merchant. It now
 * holds. Check this per route before adding one.
 */
const EARLY_MOUNT_PATHS = new Set(["/", "/popup", "/integrations", "/data-privacy"]);

/**
 * Statuses that count as "paying, leave them alone".
 *
 * `pending` is included deliberately: App Pricing never emits it, but a legacy
 * Billing API subscription can sit in it mid-approval during cutover, and
 * locking a merchant out while Shopify processes their charge is the wrong way
 * to be wrong.
 */
const ACTIVE_SUBSCRIPTION_STATUSES = new Set(["active", "pending"]);

/**
 * The boot read. Everything the app needs from the shop record.
 *
 * ⚠️ Module scope, and the SINGLE definition of these fields. BILLING_SELECT below
 * must stay a strict subset — if the two drift, a field the paywall reads stops being
 * refreshed by the live query and the merge silently serves the boot-time value forever.
 */
const SHOP_SELECT = {
  id: true,
  // `name` is rendered by routes/welcome.jsx ("You're all set, {name}").
  // It was never selected, so that greeting had never once shown a name.
  name: true,
  // Both prefill the Cal.com booking form in components/SupportHub/SupportHub.jsx.
  //
  // ⚠️ Neither was selected, so SupportHub received `shopEmail=''` and passed an EMPTY
  // `email` into the Cal embed config. @calcom/embed-core serialises config with a
  // `!== undefined` guard (dist/embed/embed.js), so an empty string is still written and
  // the iframe URL carries a literal `?email=`. Cal.com reads that as "no prefill" and
  // falls back to the LOGGED-IN Cal.com session — so the field showed the app owner's own
  // address to anyone logged into Cal, and nothing at all to a merchant.
  //
  // Measured 2026-08-23 against the live booking page: `?name=X&email=` reproduces the bug
  // exactly, while a non-empty `?email=` overrides the session on both cal.com and the
  // `app.cal.com/…?embed=` path this app loads. Supplying a real value is the whole fix.
  email: true,
  // Cal asks "Your name", not "Your store" — this is the person, `name` is the fallback.
  shopOwner: true,
  currency: true,
  moneyFormat: true,
  moneyWithCurrencyFormat: true,
  usedTrialMinutes: true,
  trialStartedAt: true,
  trialDays: true,
  currentPlan: true,
  subscriptionStatus: true,
  myshopifyDomain: true,  // For theme extension deep linking
  domain: true,           // Merchant's real custom domain (storefront preview links)
  // Countdown "fixed end date" is authored as a naive wall-clock string; it needs a
  // timezone to become one absolute instant instead of a different instant per
  // shopper. See components/Preview/VisualEditorV2/components/PropertyPanel.jsx.
  ianaTimezone: true,
};

/**
 * EXACTLY the fields the paywall at the bottom of this file reads, and nothing else.
 *
 * `availableTrialDays` derives from usedTrialMinutes / trialStartedAt / trialDays (see the
 * useMemo below), and the gate itself also reads subscriptionStatus. `currentPlan` is here
 * because billing.jsx and the plan badges read it off this context. Keeping the set minimal
 * keeps the live subscription's payload — and its re-render blast radius — small.
 */
const BILLING_SELECT = {
  id: true,
  currentPlan: true,
  subscriptionStatus: true,
  trialDays: true,
  trialStartedAt: true,
  usedTrialMinutes: true,
};

/**
 * React component that fetches shop data
 * Key features:
 *  - Fetch shop
 *  - Gate usage of the app if trial is over and merchant hasn't paid
 */
export default ({ children }) => {
  const [show, setShow] = useState(false);
  const [bannerContext, setBannerContext] = useState("");
  const bannerElRef = useRef(null);

  /**
   * ⚠️ THIS QUERY IS DELIBERATELY *NOT* `live: true`, AND MUST NOT BECOME SO AGAIN.
   *
   * `live: true` compiles to an `@live` directive that the Gadget client routes through
   * `subscriptionExchange` (`GadgetConnection.js:334-336`, via `documentContainsLiveQuery`).
   * That transport is **WebSocket-only — there is no HTTP path**. So a live shop query
   * cannot resolve until the socket handshake completes, and BOTH render gates in this file
   * (the guard below and the cover at the bottom) plus `routes/index.jsx`'s `|| !shop` block
   * paint on it.
   *
   * Measured on production 2026-08-17 (`admin.shopify.com_home.har`): the socket's
   * connection_ack took 6.6 s and the shop record did not land until **t=14.3 s**, against
   * 262-738 ms for every plain HTTP query in the same boot. That was a 10-second blank
   * screen. Shopify's own argus socket acked in 344 ms on the same page, so this was our
   * endpoint, not the network.
   *
   * The freshness the paywall needs is preserved by the live query immediately below.
   */
  const [{ data: bootShop, fetching: fetchingShop, error: errorFetchingShop }] =
    useFindFirst(api.shopifyShop, { select: SHOP_SELECT });

  /**
   * The live billing read. GATES NOTHING — it must never be allowed to block paint.
   *
   * ⚠️ THE REASON THIS EXISTS IS **NOT** SHOPIFY WEBHOOKS. That was the old comment here and
   * it is now provably false: Shopify sends no subscription webhooks for App Pricing
   * enrolments after 2026-04-28, `shopifyAppSubscription/actions/{update,delete}.js` both
   * hard-return on `APP_PRICING_MIGRATION_DISABLED`, and `checkSubscriptionStatus` was
   * retired 2026-08-15. Anyone auditing that chain would correctly conclude the stated
   * reason is dead — and deleting this query on that basis BREAKS PAYING MERCHANTS.
   *
   * The real reason is a tree-shape accident. This provider lives in `Layout`, ABOVE the
   * router outlet, and `routes/welcome.jsx:219` leaves via `navigate("/", { replace: true })`
   * — a CLIENT-SIDE navigation. So ShopProvider never remounts across the post-payment
   * handoff and the boot query above never re-runs:
   *
   *   pay → /welcome (billing-exempt) → syncShopSubscription writes 'active' to the DB
   *       → navigate("/")  ← this provider still mounted, still holding the OLD status
   *       → the paywall reads the pre-payment value → BillingPage for someone who just paid
   *
   * This subscription is the only thing that pushes that DB write into the mounted provider.
   * It stays until `welcome.jsx` gains an explicit refetch call (see the "Deferred" section
   * of docs/architecture/admin-load-time.md).
   *
   * ⚠️ DELIBERATELY NOT `pause`d, and this is a correctness constraint, not a preference.
   * Pausing it until `bootShop` lands (~4s) would delay the socket handshake, which measures
   * up to 6.6s cold — so it could still be connecting at ~10s. Meanwhile welcome.jsx's sync
   * measures 8-18s and navigates 900ms after it resolves, i.e. possibly at ~8.9s. Invert
   * those two and the dashboard renders with the PRE-PAYMENT status still merged in, and
   * paywalls a merchant who has just paid.
   *
   * Leaving it unpaused makes this subscription's timing identical to the pre-split
   * behaviour, which is exactly what we want: the split is meant to stop the socket gating
   * PAINT, not to change when the socket connects. It costs nothing on the critical path
   * because nothing waits on it any more.
   */
  const [{ data: liveBilling }] = useFindFirst(api.shopifyShop, {
    select: BILLING_SELECT,
    live: true,
  });

  /**
   * ⚠️ `useMemo` IS LOAD-BEARING, not tidiness — but be precise about WHAT it buys, because
   * the obvious claim is wrong. It does NOT stop context consumers re-rendering: the provider
   * below passes `value={{ shop }}`, a fresh object literal on every render, so all 15
   * consumers re-render whenever this component does, memo or not. (That is unchanged from
   * before the split — urql also handed back a stable `data` object.)
   *
   * What it DOES buy is a stable identity for the two places that put `shop` itself in a
   * dependency array — the `availableTrialDays` useMemo just below, and the message-listener
   * effect in routes/index.jsx. Without it, an unmemoised merge would produce a new object
   * every render and thrash both.
   *
   * Spread order matters: `liveBilling` wins, because it is the fresher read of exactly the
   * fields the paywall evaluates. It is `?? {}`-guarded because a paused or settled-empty
   * Gadget query yields undefined, and spreading that must be a no-op rather than wiping the
   * boot values.
   */
  const shop = useMemo(
    () => (bootShop ? { ...bootShop, ...(liveBilling ?? {}) } : bootShop),
    [bootShop, liveBilling]
  );

  /**
   * Boot mark for the instant the shop query lands.
   *
   * ⚠️ IN THE RENDER BODY ON PURPOSE, NOT IN A useEffect. An effect fires after
   * paint, so it would time the browser's paint rather than the data arriving,
   * and every downstream segment (auth_to_shop, shop→editor) would be wrong by
   * that amount. markBoot is idempotent — first write wins (utils/bootTiming.js)
   * — so calling it on every render, including StrictMode's double invoke, is
   * safe and is exactly how `auth_ready` is marked in App.jsx.
   */
  if (shop) markBoot("shop_ready");

  // Compute trial days synchronously during render (avoids extra re-render from useEffect+setState)
  const availableTrialDays = useMemo(() => {
    if (!shop) return 0;
    return trialCalculations(
      shop.usedTrialMinutes,
      shop.trialStartedAt,
      new Date(),
      shop.trialDays
    ).availableTrialDays;
  }, [shop]);

  // Path-based paywall exemption — see BILLING_EXEMPT_PATHS above and the
  // comment at the gate. Mirrors the isPopup exemption in App.jsx:244.
  const location = useLocation();
  const isBillingExempt = BILLING_EXEMPT_PATHS.has(location.pathname);
  // Mount-timing only — see EARLY_MOUNT_PATHS. Deliberately a separate flag
  // from isBillingExempt so the two concepts can never be conflated: this one
  // must NOT appear anywhere in the paywall condition.
  const isEarlyMount = EARLY_MOUNT_PATHS.has(location.pathname);

  const handleDismiss = useCallback(() => {
    setShow(false);
  }, []);

  /**
   * <s-banner>'s X is a NATIVE custom-element event, not a React synthetic one.
   * React 18.2 only wires its own synthetic events onto custom elements, so an
   * `onDismiss={...}` JSX prop is silently dropped — no error, an X that renders
   * and does nothing, and a critical banner the merchant can never close.
   * Attaching on a ref is the pattern used for the same reason in
   * `components/Preview/PreviewOnStoreButton.jsx`.
   *
   * Depends on `show` because the banner is conditionally rendered: the ref is
   * null until it mounts.
   */
  useEffect(() => {
    const el = bannerElRef.current;
    if (!el) return;
    el.addEventListener("dismiss", handleDismiss);
    el.addEventListener("afterhide", handleDismiss);
    return () => {
      el.removeEventListener("dismiss", handleDismiss);
      el.removeEventListener("afterhide", handleDismiss);
    };
  }, [show, handleDismiss]);

  // useEffect for showing a banner if there's and error fetching shop information
  useEffect(() => {
    if (!fetchingShop && errorFetchingShop) {
      setBannerContext(errorFetchingShop.message);
      setShow(true);
    } else if (fetchingShop) {
      setShow(false);
    }
  }, [fetchingShop, errorFetchingShop]);

  /**
   * ⚠️ THE EXEMPTION IS PART OF THE CONDITION, NOT A BRANCH INSIDE IT.
   *
   * /welcome is exempt from this whole gate — including the error state below.
   * It is the App Pricing return landing, and it needs NOTHING from this query:
   * its sync derives the shop from the Gadget session, which AuthenticatedApp
   * has already resolved before this provider mounts.
   *
   * An earlier revision put the error branch ABOVE the exemption check, which
   * quietly recreated the deadlock the exemption exists to prevent: a merchant
   * who had just paid, hitting a transient shop-query failure, got "We couldn't
   * load your store" and syncShopSubscription never dispatched at all. Keep the
   * exemption in the guard condition so no branch inside can escape it.
   *
   * Falling through renders welcome.jsx with `shop` undefined. That is safe and
   * already handled: welcome.jsx reads `useContext(ShopContext) ?? {}` then
   * `shop?.name`, and its heading falls back to "You're all set". The only other
   * child is <ChatWidget/>, which touches no shop data at all. Every other
   * ShopContext consumer lives on a non-exempt route and cannot mount in this
   * state.
   *
   * Bonus: skipping the gate also stops WelcomePage being UNMOUNTED and
   * remounted when the query lands — this early return and the provider below
   * have different roots, so React tore the page down mid-sync.
   */
  /**
   * ⚠️ THE EARLY-MOUNT ESCAPE IS ALSO PART OF THE CONDITION, for the same
   * reason as the exemption above: no branch inside may be able to escape it.
   *
   * `!(isEarlyMount && fetchingShop)` is deliberately narrower than
   * `!isEarlyMount`. It only releases the gate while the query is IN FLIGHT:
   *   • early-mount + fetching        → falls through, children mount, the
   *                                     cover at the bottom hides them
   *   • early-mount + settled + shop  → normal render, cover unmounts
   *   • early-mount + settled + NO shop → still enters this block, and since
   *                                     !fetchingShop, still reaches the error
   *                                     page. A broken query must never be
   *                                     served as a permanent skeleton.
   */
  if ((fetchingShop || !shop) && !isBillingExempt && !(isEarlyMount && fetchingShop)) {
    /**
     * A settled query with no shop used to render a spinner FOREVER, app-wide,
     * with no message and no way out: the early return sits above the error
     * banner further down, so `show`/`bannerContext` were unreachable whenever
     * `!shop`. Surface it instead — @gadgetinc/react's guidance is to handle
     * loading and error states separately, and "loading" and "broken" are
     * indistinguishable to the person watching.
     *
     * ⚠️ Keyed on `!fetchingShop` alone, NOT on `errorFetchingShop`. Unlike the
     * backend `findFirst` (which throws on empty), the useFindFirst HOOK reports
     * an empty result as `data: null` with NO error set. Gating this branch on
     * the error object would leave that state — settled, no shop, no error —
     * falling through to the spinner forever, which is the same bug with a
     * narrower trigger.
     */
    if (!fetchingShop) {
      return (
        <s-page heading="We couldn't load your store">
          <s-section>
            <s-stack direction="block" gap="base">
              <s-banner tone="critical" heading="Something went wrong">
                <s-text>
                  {errorFetchingShop?.message ??
                    "We couldn't find your store details. Reload to try again."}
                </s-text>
              </s-banner>
              <s-stack direction="inline" justifyContent="start">
                <s-button
                  variant="primary"
                  onClick={() => window.location.reload()}
                >
                  Try again
                </s-button>
              </s-stack>
            </s-stack>
          </s-section>
        </s-page>
      );
    }

    return <BootShell variant={bootShellVariant(location.pathname)} />;
  }

  return (
    <ShopContext.Provider
      value={{
        shop,
      }}
    >
      {/*
        ⚠️ `!isBillingExempt` here too — the guard above is not sufficient on its
        own. The error effect sets `show` on any errorFetchingShop regardless of
        route, and /welcome now FALLS THROUGH that guard rather than returning
        early, so without this a transient shop-query error would paint a
        critical banner in its own <s-page> stacked directly above
        PlanActivationScreen's <s-page> — two page elements, and a "something
        failed" message shown to the merchant who has just paid.

        That is the exact outcome the exemption exists to prevent; it would just
        have arrived via the banner instead of the early return. The shop query
        is irrelevant to what /welcome is doing, so its failures are not the
        merchant's problem while the sync is still running.
      */}
      {show && !isBillingExempt && (
        <s-page>
          <s-banner
            ref={bannerElRef}
            heading={bannerContext}
            tone="critical"
            dismissible
          />
        </s-page>
      )}
      {/*
        Whole-app paywall. Two changes on 2026-08-15 (Shopify App Pricing):

        1. /welcome is EXEMPT. It is the App Pricing return landing, and its whole
           job is to run syncShopSubscription — which is what writes the state this
           gate reads. Without the exemption: gate blocks → /welcome never mounts →
           sync never runs → gate stays blocked. A merchant who just paid would be
           trapped in the exact redirect loop reported on community.shopify.com.
           Same shape as the isPopup exemption in App.jsx:244.

        2. `subscriptionStatus === 'pending'` now also passes. App Pricing never
           produces `pending`, but a legacy Billing API subscription can still be
           mid-approval during cutover, and locking those merchants out of the app
           while Shopify processes the charge is the wrong failure direction.

        ⚠️ THIS GATE IS ARMED. An earlier revision of this comment claimed it was
        "currently INERT in practice" because trialStartedAt was only ever set at
        plan selection. That is no longer true — syncShopSubscription now writes
        trialStartedAt from the subscription's createdAt, and live reads of
        production found TWO OF FOUR installed shops sitting at
        availableTrialDays === 0, held inside the app solely by
        subscriptionStatus === 'active' (docs/architecture/admin-load-time.md:299).

        So a stale or missing subscriptionStatus here paywalls a paying merchant.
        That is the entire reason the live billing query at the top of this file
        still exists — read its comment before touching either.
      */}
      {/*
        ⚠️ THE LEADING `shop &&` IS LOAD-BEARING — do not "simplify" it away as
        redundant with the `shop?.` below.

        Before the early-mount path existed, this expression could only run with
        a settled, present `shop`, so it needed no null handling. It can now run
        with `shop === undefined`, and in that state EVERY term flips the wrong
        way at once:
          • availableTrialDays  → the useMemo above returns 0 when !shop, so
                                  `!availableTrialDays` is TRUE
          • subscriptionStatus  → ACTIVE_SUBSCRIPTION_STATUSES.has(undefined) is
                                  FALSE, so its negation is TRUE
          • isBillingExempt     → /popup is not exempt, so this is TRUE too
        All three true ⇒ the paywall fires and renders <BillingPage/> INSTEAD of
        the editor, on a fully paid shop, for the whole in-flight window. The
        cover below would hide it visually while BillingPage mounted, ran its
        own queries, and — worse — {children} would never mount, which destroys
        the entire point of the early mount.

        `shop &&` makes "we don't know yet" mean "don't paywall yet". It changes
        nothing once the query lands: with `shop` present the term is truthy and
        the condition is byte-for-byte the original.
      */}
      {shop && !isBillingExempt && !availableTrialDays && !ACTIVE_SUBSCRIPTION_STATUSES.has(shop?.subscriptionStatus) ? (
        <Suspense fallback={<BillingSkeleton />}>
          <BillingPage />
        </Suspense>
      ) : (
        <>
          {/* {!!availableTrialDays && (
            <s-page>
              <s-banner
                heading="Welcome to the app! You are currently on a trial period."
                tone="info"
              >
                <s-paragraph>
                  The trial will end in <strong>{availableTrialDays}</strong>{" "}
                  days.
                </s-paragraph>
              </s-banner>
            </s-page>
          )} */}
          {children}
        </>
      )}
      {/*
        Early-mount cover. The merchant sees the editor skeleton; behind it the
        real editor is already mounted and its campaign query is already in the
        air. It lifts when `shop` arrives.

        ⚠️ A SIBLING, NEVER A WRAPPER. It is tempting to write
        `{!shop ? <Cover>{children}</Cover> : children}`. That changes the
        ELEMENT TYPE at this position when the query lands, so React reconciles
        it as a different node, UNMOUNTS the entire editor subtree and mounts a
        fresh one — throwing away the campaign query, the element tree and every
        piece of editor state that the early mount existed to buy. Rendering the
        cover after {children} keeps {children}'s position in the child list
        stable across the transition, so the only thing that unmounts is the
        cover itself.

        ⚠️ GATED ON `!shop`, NOT `!fetchingShop`. hooks/useCurrency.js:21-26
        reads `shop?.currency` / `shop?.moneyFormat` straight out of this
        context and SILENTLY falls back to USD and '${{amount}}' when shop is
        undefined — no error, just wrong money in the editor. So the cover must
        last exactly as long as `shop` is undefined, which is a strictly longer
        window than `fetchingShop` (the query can settle a tick before the value
        is committed here, and the settled-but-empty case never resolves at all;
        in that case the error page above has already taken over instead).

        aria-hidden because the live editor underneath is the real content for
        assistive tech; this is a purely visual placeholder. The z-index is the
        32-bit max so it beats App Bridge / Polaris overlays inside the modal.

        ⚠️ THE COVER MUST DRAW THE SAME PICTURE THE GATE ABOVE WOULD HAVE DRAWN,
        per route. /popup gets CampaignEditorSkeleton (the editor's own shell, in
        the modal's realm); every other early-mount path gets the BootShell, which
        is what the gate, App.jsx's router and auth gates, and index.html's static
        markup all render — so adding "/" to EARLY_MOUNT_PATHS does not introduce
        a new frame into the boot, it just moves work behind the existing one.

        The backdrop colour follows suit: `.ns-boot` paints #f1f1f1 over
        `min-height: 100vh` (see the boot stylesheet in index.html), and the
        embedded iframe is resized to CONTENT height, which the mounted dashboard
        can push past 100vh. A white cover would then show a band below the shell.
      */}
      {isEarlyMount && !shop && (
        <div
          aria-hidden="true"
          data-ns-shop-gate="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147483647,
            background: location.pathname === '/popup' ? '#fff' : '#f1f1f1',
          }}
        >
          {location.pathname === '/popup' ? (
            <CampaignEditorSkeleton />
          ) : (
            <BootShell variant={bootShellVariant(location.pathname)} />
          )}
        </div>
      )}
    </ShopContext.Provider>
  );
};
